import { API } from '@/core/Api';
import { getVerifiedToken } from '@/core/U';

// Anthropic Claude AI 서비스
export class AnthropicAI {
  /**
   * Claude 3.5 Sonnet 모델로 메시지 생성
   * @param content 사용자 메시지 내용
   * @param options 추가 옵션들
   * @returns Claude 응답
   */
  async generateWithSonnet(
    content: string,
    options: {
      max_tokens?: number; // 최대 토큰 수
      temperature?: number; // 창의성 (0-1)
      model?: string; // 모델명 (선택사항)
      prompt_key?: string;
      prompt_variable?: object;
    } = {},
  ) {
    const requestData = {
      model: options.model || 'claude-sonnet-4-20250514',
      messages: [
        {
          role: 'user',
          content: content,
          prompt_key: options.prompt_key,
          prompt_variable: options.prompt_variable,
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.7,
    };

    const response = await API.post('/api/ai/anthropic/messages', requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Claude Sonnet 메시지 생성에 실패했습니다.');
    }
  }

  /**
   * Claude Opus 4 모델로 메시지 생성
   * @param content 사용자 메시지 내용
   * @param options 추가 옵션들
   * @returns Claude 응답
   */
  async generateWithOpus(
    content: string,
    options: {
      max_tokens?: number;
      temperature?: number;
    } = {},
  ) {
    const requestData = {
      model: 'claude-opus-4-20250514',
      messages: [
        {
          role: 'user',
          content: content,
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.8,
    };

    const response = await API.post('/api/ai/anthropic/messages', requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Claude Opus 메시지 생성에 실패했습니다.');
    }
  }

  /**
   * Claude 확장 사고 기능 사용
   * @param content 복잡한 문제나 질문
   * @param options 사고 옵션들
   * @returns 사고 과정이 포함된 응답
   */
  async generateWithThinking(
    content: string,
    options: {
      max_tokens?: number;
      thinking_budget?: number; // 사고 예산 (토큰 수)
      temperature?: number;
    } = {},
  ) {
    const requestData = {
      model: 'claude-sonnet-4-20250514',
      messages: [
        {
          role: 'user',
          content: content,
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.1,
      enableThinking: true,
      thinkingBudget: options.thinking_budget || 15000,
    };

    const response = await API.post('/api/ai/anthropic/messages', requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Claude 확장 사고 생성에 실패했습니다.');
    }
  }

  /**
   * Claude Sonnet 스트리밍 생성
   * @param content 사용자 메시지 내용
   * @param onChunk 스트리밍 데이터 처리 콜백
   * @param options 추가 옵션들
   */
  async generateWithSonnetStream(
    content: string,
    onChunk: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string; // 모델명 (선택사항)
      enableThinking?: boolean; // thinking 활성화/비활성화
      thinkingBudget?: number; // thinking 토큰 예산
      prompt_key?: string;
      prompt_variable?: object;
    } = {},
  ): Promise<void> {
    const requestData = {
      model: options.model || 'claude-sonnet-4-20250514',
      messages: [
        {
          role: 'user',
          content: content,
          prompt_key: options.prompt_key,
          prompt_variable: options.prompt_variable,
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.7,
      stream: true,
      //thinking param 추가
      enableThinking: options.enableThinking,
      thinkingBudget: options.thinkingBudget,
    };

    // 인증 토큰 가져오기
    const token = await getVerifiedToken();

    let response: Response | undefined;
    let retryCount = 0;
    const maxRetries = 3;

    while (retryCount < maxRetries) {
      response = await fetch(`${process.env.NEXT_PUBLIC_API_SERVER}/api/ai/anthropic/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        break; // 성공하면 루프 종료
      }

      if (response.status === 429) {
        retryCount++;
        if (retryCount < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 10000); // 지수 백오프 (최대 10초)
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
      }

      // 429가 아닌 다른 오류이거나 최대 재시도 횟수 초과
      throw new Error(`Claude Stream API 오류: ${response.statusText} (시도 횟수: ${retryCount})`);
    }

    if (!response) {
      throw new Error('Response is undefined after retry attempts');
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body is null');

    const decoder = new TextDecoder();

    try {
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // 마지막 불완전한 라인은 버퍼에 보관
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6).trim();

            // 종료 신호 확인
            if (data === '[DONE]') return;

            // 빈 데이터 건너뛰기
            if (!data) continue;

            try {
              const parsed = JSON.parse(data);

              // Anthropic API 이벤트 형식에 따라 파싱
              if (parsed.type === 'content_block_delta' && parsed.delta?.text) {
                onChunk(parsed.delta.text);
              } else if (
                parsed.type === 'content_block_delta' &&
                parsed.delta?.type === 'text_delta'
              ) {
                onChunk(parsed.delta.text);
              }
            } catch (e) {
              console.warn('JSON 파싱 오류:', data);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Claude Opus 스트리밍 생성
   * @param content 사용자 메시지 내용
   * @param onChunk 스트리밍 데이터 처리 콜백
   * @param options 추가 옵션들
   */
  async generateWithOpusStream(
    content: string,
    onChunk: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
    } = {},
  ): Promise<void> {
    const requestData = {
      model: 'claude-opus-4-20250514',
      messages: [
        {
          role: 'user',
          content: content,
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.8,
      stream: true,
    };

    // 인증 토큰 가져오기
    const token = await getVerifiedToken();

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_SERVER}/api/ai/anthropic/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`Claude Stream API 오류: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body is null');

    const decoder = new TextDecoder();

    try {
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // 마지막 불완전한 라인은 버퍼에 보관
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6).trim();

            // 종료 신호 확인
            if (data === '[DONE]') return;

            // 빈 데이터 건너뛰기
            if (!data) continue;

            try {
              const parsed = JSON.parse(data);

              // Anthropic API 이벤트 형식에 따라 파싱
              if (parsed.type === 'content_block_delta' && parsed.delta?.text) {
                onChunk(parsed.delta.text);
              } else if (
                parsed.type === 'content_block_delta' &&
                parsed.delta?.type === 'text_delta'
              ) {
                onChunk(parsed.delta.text);
              }
            } catch (e) {
              console.warn('JSON 파싱 오류:', data);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * 이미지 분석 (Claude Vision)
   * @param content 이미지에 대한 질문
   * @param imageUrl 분석할 이미지 URL (base64 data URL)
   * @param options 추가 옵션들
   * @returns 이미지 분석 결과
   */
  async analyzeImage(
    content: string,
    imageUrl: string,
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string;
    } = {},
  ) {
    const requestData = {
      model: options.model || 'claude-sonnet-4-20250514',
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: content,
            },
            {
              type: 'image_url',
              image_url: {
                url: imageUrl,
              },
            },
          ],
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.7,
    };

    const response = await API.post('/api/ai/anthropic/messages', requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지 분석에 실패했습니다.');
    }
  }

  /**
   * 여러 이미지 분석 (Claude Vision)
   * @param content 이미지들에 대한 질문
   * @param imageUrls 분석할 이미지 URL들 (base64 data URL)
   * @param options 추가 옵션들
   * @returns 이미지 분석 결과
   */
  async analyzeMultipleImages(
    content: string,
    imageUrls: string[],
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string;
      enableThinking?: boolean; // thinking 활성화/비활성화
      thinkingBudget?: number; // thinking 토큰 예산
      prompt_key?: string;
      prompt_variable?: object;
    } = {},
  ) {
    const contentArray: Array<{
      type: string;
      text?: string;
      prompt_key?: string;
      prompt_variable?: any;
      image_url?: { url: string };
    }> = [
      {
        type: 'text',
        text: content,
        prompt_key: options.prompt_key,
        prompt_variable: options.prompt_variable,
      },
    ];

    // 각 이미지를 content 배열에 추가
    imageUrls.forEach((url) => {
      contentArray.push({
        type: 'image_url',
        image_url: {
          url: url,
        },
      });
    });

    const requestData = {
      model: options.model || 'claude-sonnet-4-20250514',
      messages: [
        {
          role: 'user',
          content: contentArray,
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.7,
      enableThinking: options.enableThinking,
      thinkingBudget: options.thinkingBudget,
    };

    const response = await API.post('/api/ai/anthropic/messages', requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '다중 이미지 분석에 실패했습니다.');
    }
  }

  /**
   * 이미지 분석 스트리밍 (Claude Vision)
   * @param content 이미지에 대한 질문
   * @param imageUrl 분석할 이미지 URL (base64 data URL)
   * @param onChunk 스트리밍 데이터 처리 콜백
   * @param options 추가 옵션들
   */
  async analyzeImageWithStream(
    content: string,
    imageUrl: string,
    onChunk: (chunk: string) => void,
    options: {
      max_tokens?: number;
      temperature?: number;
      model?: string;
      enableThinking?: boolean; // thinking 활성화/비활성화
      thinkingBudget?: number; // thinking 토큰 예산
      prompt_key?: string;
      prompt_variable?: object;
    } = {},
  ): Promise<void> {
    const requestData = {
      model: options.model || 'claude-sonnet-4-20250514',
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: content,
              prompt_key: options.prompt_key,
              prompt_variable: options.prompt_variable,
            },
            {
              type: 'image_url',
              image_url: {
                url: imageUrl,
              },
            },
          ],
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.7,
      stream: true,
      enableThinking: options.enableThinking,
      thinkingBudget: options.thinkingBudget,
    };

    // 인증 토큰 가져오기
    const token = await getVerifiedToken();

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_SERVER}/api/ai/anthropic/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`이미지 분석 스트리밍 오류: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body is null');

    const decoder = new TextDecoder();

    try {
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // 마지막 불완전한 라인은 버퍼에 보관
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6).trim();

            // 종료 신호 확인
            if (data === '[DONE]') return;

            // 빈 데이터 건너뛰기
            if (!data) continue;

            try {
              const parsed = JSON.parse(data);

              // Anthropic API 이벤트 형식에 따라 파싱
              if (parsed.type === 'content_block_delta' && parsed.delta?.text) {
                onChunk(parsed.delta.text);
              } else if (
                parsed.type === 'content_block_delta' &&
                parsed.delta?.type === 'text_delta'
              ) {
                onChunk(parsed.delta.text);
              }
            } catch (e) {
              console.warn('JSON 파싱 오류:', data);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Claude 웹 검색 도구 사용
   * @param content 검색이 필요한 질문
   * @param options 추가 옵션들
   * @returns 웹 검색 결과가 포함된 응답
   */
  async generateWithTools(
    content: string,
    options: {
      max_tokens?: number;
      temperature?: number;
    } = {},
  ) {
    const requestData = {
      model: 'claude-sonnet-4-20250514',
      messages: [
        {
          role: 'user',
          content: content,
        },
      ],
      max_tokens: options.max_tokens || 2000,
      temperature: options.temperature || 0.3,
    };

    const response = await API.post('/api/ai/anthropic/messages', requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || 'Claude 도구 사용에 실패했습니다.');
    }
  }

  /**
   * 사용 가능한 Claude 모델 목록 조회
   * @returns 모델 목록
   */
  async getAvailableModels() {
    const response = await API.get('/api/ai/anthropic/models');

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '모델 목록 조회에 실패했습니다.');
    }
  }

  /**
   * 레거시 완료 엔드포인트 사용
   * @param content 메시지 내용
   * @param options 추가 옵션들
   * @returns 응답
   */
  async generateCompletion(
    content: string,
    options: {
      model?: string;
      maxTokens?: number;
      temperature?: number;
    } = {},
  ) {
    const requestData = {
      provider: 'anthropic',
      model: options.model || 'claude-sonnet-4-20250514',
      messages: [
        {
          role: 'user',
          content: content,
        },
      ],
      maxTokens: options.maxTokens || 150,
      temperature: options.temperature || 0.5,
    };

    const response = await API.post('/api/ai/anthropic/completion', requestData);

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '레거시 완료 요청에 실패했습니다.');
    }
  }

  /**
   * Claude 테스트용 예제 프롬프트들
   */
  getExamplePrompts() {
    return {
      sonnet: [
        '기계 학습을 음식 요리에 비유해서 설명해주세요. 150자 이내로.',
        'AI가 창작 분야에 미칠 긍정적 영향 3가지를 설명해주세요.',
        '효율적인 프로그래밍 학습법을 단계별로 알려주세요.',
      ],
      opus: [
        '인공 의식의 철학적 함의에 대한 간단하면서도 깊이 있는 분석을 작성해주세요.',
        '창의성과 인공지능의 관계에 대해 철학적으로 논의해주세요.',
        '인간과 AI의 협력이 만들어낼 미래 사회의 모습을 그려보세요.',
      ],
      thinking: [
        '한 회사에 3개의 공장이 있습니다. A공장은 전체 생산량의 40%, B공장은 35%, C공장은 25%를 생산합니다. 불량률은 각각 2%, 3%, 1%입니다. 무작위로 선택한 제품이 불량품일 때, B공장에서 나올 확률은?',
        '체스판에서 말이 한 칸에서 시작해서 정확히 n번 이동 후 원래 자리로 돌아올 수 있는 방법의 수를 구하는 알고리즘을 설계하세요.',
        '두 개의 주사위를 던져서 나온 수의 곱이 6의 배수가 될 확률을 구하세요.',
      ],
      tools: [
        '양자 컴퓨팅 연구의 최신 발전 사항은 무엇인가요? 최근 정보를 검색해주세요.',
        '2024년 AI 분야의 가장 중요한 발견이나 발전은 무엇인가요?',
        '현재 기후 변화 대응을 위한 최신 기술 동향을 찾아보세요.',
      ],
      vision: [
        '이 이미지에서 무엇을 볼 수 있나요? 자세히 설명해주세요.',
        '이미지의 색상, 구성, 스타일을 분석해주세요.',
        '이 차트나 그래프의 데이터를 해석해주세요.',
        '이 이미지에서 텍스트를 추출하고 요약해주세요.',
      ],
    };
  }

  /**
   * Claude 모델별 특징 설명
   */
  getModelDescriptions() {
    return {
      'claude-sonnet-4-20250514':
        '균형잡힌 성능의 Sonnet 4 모델 - 일반적인 대화와 작업에 적합, 확장 사고 기능 및 이미지 분석 지원',
      'claude-opus-4-20250514':
        '최고 성능의 Opus 4 모델 - 복잡한 추론과 창작 작업에 최적, 고급 이미지 분석 기능 포함',
    };
  }
}
